import { ApiError, storedSite, sessionHeaders } from '../../api';

export interface RuleView {
  readonly id: string;
  readonly code: string;
  readonly version: number;
  readonly name: string;
  readonly condition: Record<string, unknown>;
  readonly action: Record<string, unknown>;
  readonly priority: number;
  readonly siteIds: readonly string[];
  readonly validFrom: string | null;
  readonly validTo: string | null;
  readonly status: 'draft' | 'active' | 'archived';
  readonly publishedAt: string | null;
  readonly createdAt: string;
}

export interface PreviewSample {
  readonly orderId: string;
  readonly customerId: string;
  readonly totalMinor: number;
  readonly deltaMinor: number;
  readonly occurredAt: string;
}

export interface PreviewResult {
  readonly ordersScanned: number;
  readonly ordersAffected: number;
  readonly customersAffected: number;
  readonly deltaMinor: number;
  readonly truncated: boolean;
  readonly samples: readonly PreviewSample[];
}

export interface AuditRow {
  readonly id: string;
  readonly action: string;
  readonly createdAt: string;
  readonly actor: unknown;
}

export interface VersionsResult {
  readonly code: string;
  readonly versions: readonly RuleView[];
  readonly history: readonly AuditRow[];
}

export interface AccountView {
  readonly accountId: string;
  readonly customerId: string;
  readonly balanceMinor: number;
  readonly materializedMinor: number;
  readonly driftMinor: number;
  readonly currency: string;
}

export interface LedgerRow {
  readonly id: string;
  readonly direction: 'credit' | 'debit';
  readonly amountMinor: number;
  readonly reasonCode: string;
  readonly ruleCode: string | null;
  readonly ruleVersion: number | null;
  readonly comment: string | null;
  readonly createdAt: string;
}

export interface LedgerPage {
  readonly items: readonly LedgerRow[];
  readonly nextCursor: string | undefined;
}

export interface AdjustmentReason {
  readonly code: string;
  readonly labelRu: string;
  readonly labelKk: string;
  readonly allowedDirection: 'credit' | 'debit' | 'both';
}

export interface TierView {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly entryCondition: Record<string, unknown>;
  readonly sortOrder: number;
}

export interface ReconciliationFinding {
  readonly id: string;
  readonly customerId: string;
  readonly materializedMinor: number;
  readonly ledgerMinor: number;
  readonly deltaMinor: number;
  readonly repaired: boolean;
}

export interface ReconciliationReport {
  readonly run: {
    readonly id: string;
    readonly startedAt: string;
    readonly accountsChecked: number;
    readonly findingsCount: number;
    readonly status: string;
  };
  readonly findings: readonly ReconciliationFinding[];
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  idempotencyKey?: string,
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  Object.assign(headers, sessionHeaders());
  const site = storedSite();
  if (site) headers['x-moika-site'] = site;
  if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;

  const res = await fetch(path, {
    method,
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new ApiError(
      String(payload['error'] ?? 'E00.internal'),
      String(payload['message'] ?? payload['error'] ?? 'Ошибка запроса'),
      res.status,
    );
  }
  return payload['data'] as T;
}

/** Правило подсказки оператору (ТЗ М3.3). */
export type SuggestionKind = 'next_service' | 'upgrade' | 'subscription';

export interface SuggestionCondition {
  readonly segmentCodes?: readonly string[];
  readonly minVisits?: number;
  readonly windowDays?: number;
  readonly fromServiceCodes?: readonly string[];
  readonly minDaysSinceService?: number;
}

export interface SuggestionRuleView {
  readonly id: string;
  readonly kind: SuggestionKind;
  readonly title: string;
  readonly status: 'active' | 'paused';
  readonly priority: number;
  readonly siteIds: readonly string[];
  readonly condition: SuggestionCondition;
  readonly targetServiceCode: string | null;
  readonly targetPlanCode: string | null;
  readonly updatedAt: string;
}

export interface SuggestionRuleDraft {
  readonly kind: SuggestionKind;
  readonly title: string;
  readonly status?: 'active' | 'paused';
  readonly priority?: number;
  readonly siteIds?: readonly string[];
  readonly condition?: SuggestionCondition;
  readonly targetServiceCode?: string | null;
  readonly targetPlanCode?: string | null;
}

export function listSuggestionRules(): Promise<readonly SuggestionRuleView[]> {
  return request<readonly SuggestionRuleView[]>('GET', '/api/v1/loyalty/suggestions');
}

export function createSuggestionRule(draft: SuggestionRuleDraft): Promise<SuggestionRuleView> {
  return request<SuggestionRuleView>('POST', '/api/v1/loyalty/suggestions', draft);
}

export function updateSuggestionRule(
  id: string,
  draft: SuggestionRuleDraft,
): Promise<SuggestionRuleView> {
  return request<SuggestionRuleView>('PUT', `/api/v1/loyalty/suggestions/${encodeURIComponent(id)}`, draft);
}

export function newIdempotencyKey(): string {
  return `admin-${crypto.randomUUID()}`;
}

export function listRules(): Promise<readonly RuleView[]> {
  return request<readonly RuleView[]>('GET', '/api/v1/loyalty/rules');
}

export interface RuleDraft {
  readonly code: string;
  readonly name: string;
  readonly condition: Record<string, unknown>;
  readonly action: Record<string, unknown>;
  readonly priority: number;
  readonly siteIds: readonly string[];
}

export function createRule(draft: RuleDraft): Promise<RuleView> {
  return request<RuleView>('POST', '/api/v1/loyalty/rules', draft);
}

export function updateRule(id: string, patch: Partial<RuleDraft>): Promise<RuleView> {
  return request<RuleView>('PATCH', `/api/v1/loyalty/rules/${id}`, patch);
}

export function publishRule(id: string): Promise<unknown> {
  return request('POST', `/api/v1/loyalty/rules/${id}/publish`);
}

/** Вернуть публикацию архивной версии: действующая уходит в архив. */
export function restoreRule(id: string): Promise<unknown> {
  return request('POST', `/api/v1/loyalty/rules/${id}/restore`);
}

export function archiveRule(id: string): Promise<unknown> {
  return request('POST', `/api/v1/loyalty/rules/${id}/archive`);
}

export function previewRule(id: string, from: string, to: string): Promise<PreviewResult> {
  return request<PreviewResult>('POST', `/api/v1/loyalty/rules/${id}/preview`, { from, to });
}

export function ruleVersions(id: string): Promise<VersionsResult> {
  return request<VersionsResult>('GET', `/api/v1/loyalty/rules/${id}/versions`);
}

export function account(customerId: string): Promise<AccountView> {
  return request<AccountView>('GET', `/api/v1/loyalty/accounts/${customerId}`);
}

export function entries(customerId: string, since?: string): Promise<LedgerPage> {
  const query = since ? `?since=${encodeURIComponent(since)}` : '';
  return request<LedgerPage>('GET', `/api/v1/loyalty/accounts/${customerId}/entries${query}`);
}

export function adjustmentReasons(): Promise<readonly AdjustmentReason[]> {
  return request<readonly AdjustmentReason[]>('GET', '/api/v1/loyalty/adjustment-reasons');
}

export interface AdjustPayload {
  readonly customerId: string;
  readonly direction: 'credit' | 'debit';
  readonly amountMinor: number;
  readonly reasonCode: string;
  readonly comment?: string;
}

export function adjust(payload: AdjustPayload): Promise<unknown> {
  return request('POST', '/api/v1/loyalty/adjust', payload, newIdempotencyKey());
}

export function tiers(): Promise<readonly TierView[]> {
  return request<readonly TierView[]>('GET', '/api/v1/loyalty/tiers');
}

export interface SaveTierInput {
  readonly code: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly entryCondition: Record<string, number>;
  readonly benefits: Record<string, unknown>;
}

export function saveTier(input: SaveTierInput): Promise<TierView> {
  return request<TierView>('POST', '/api/v1/loyalty/tiers', input);
}

export function recalculateTiers(): Promise<{ checked: number; changed: number }> {
  return request<{ checked: number; changed: number }>(
    'POST',
    '/api/v1/loyalty/tiers/recalculate',
  );
}

export function reconciliation(): Promise<ReconciliationReport | null> {
  return request<ReconciliationReport | null>('GET', '/api/v1/loyalty/reconciliation');
}

export function runReconciliation(): Promise<ReconciliationReport> {
  return request<ReconciliationReport>('POST', '/api/v1/loyalty/reconciliation/run');
}

/**
 * Антифрод: сколько раз и на какую сумму можно начислить.
 *
 * Настройка существует с самого начала модуля и до сих пор менялась только
 * запросом в базу: экрана у неё не было, хотя spec-4 §4.6 прямо требует, чтобы
 * ограничения настраивались без разработчика.
 */
export interface GuardPolicy {
  readonly siteId: string | null;
  readonly maxAccrualsPerVisit: number;
  readonly maxAccrualsPerPeriod: number;
  readonly periodDays: number;
  readonly maxAmountPerPeriodMinor: number;
  readonly requireVehicleLink: boolean;
  readonly blockSelfReferral: boolean;
}

export function guards(siteId: string | null): Promise<GuardPolicy> {
  const suffix = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
  return request<GuardPolicy>('GET', `/api/v1/loyalty/guards${suffix}`);
}

export function saveGuards(policy: GuardPolicy): Promise<GuardPolicy> {
  return request<GuardPolicy>('POST', '/api/v1/loyalty/guards', policy);
}

/** Решение движка по заказу: начислено, отказано и почему. */
export interface ApplicationRow {
  readonly id: string;
  readonly order_id: string | null;
  readonly visit_id: string | null;
  readonly customer_id: string | null;
  readonly rule_id: string | null;
  readonly site_id: string | null;
  readonly outcome: string;
  readonly reason_code: string | null;
  readonly amount_minor: string | null;
  readonly detail: Record<string, unknown> | null;
  readonly evaluated_at: string;
}

export function applications(customerId: string): Promise<readonly ApplicationRow[]> {
  return request<readonly ApplicationRow[]>(
    'GET',
    `/api/v1/loyalty/applications?customer_id=${encodeURIComponent(customerId)}`,
  );
}

export interface RuleOptionItem {
  readonly value: string;
  readonly title: string;
  readonly note: string | null;
  readonly usable: boolean;
}

export interface RuleOptions {
  readonly sites: readonly RuleOptionItem[];
  readonly services: readonly RuleOptionItem[];
  readonly segments: readonly RuleOptionItem[];
}

/** Значения для конструктора правил: объекты, услуги и сегменты с названиями. */
export function ruleOptions(): Promise<RuleOptions> {
  return request<RuleOptions>('GET', '/api/v1/loyalty/rules/options');
}

/* ------------------------------------------------------------ приглашения */

export type ReferralSource = 'pos' | 'tablet' | 'admin' | 'link' | 'import';
export type ReferralStatus = 'pending' | 'rewarded' | 'rejected';

export interface ReferralView {
  readonly id: string;
  readonly inviteeCustomerId: string;
  readonly inviteeName: string | null;
  readonly referrerCustomerId: string;
  readonly referrerName: string | null;
  readonly code: string;
  readonly source: ReferralSource;
  readonly status: ReferralStatus;
  readonly referrerRewardMinor: number;
  readonly inviteeRewardMinor: number;
  readonly createdAt: string;
  readonly settledAt: string | null;
}

export interface ReferralSummary {
  readonly customerId: string;
  readonly code: string;
  readonly invitedBy: ReferralView | null;
  readonly invited: readonly ReferralView[];
  readonly invitedCount: number;
  readonly rewardedCount: number;
  readonly earnedMinor: number;
}

export interface ReferralPolicy {
  readonly enabled: boolean;
  readonly referrerRewardMinor: number;
  readonly inviteeRewardMinor: number;
  readonly rewardExpiresInDays: number | null;
  readonly minOrderMinor: number;
}

export function referrals(source?: ReferralSource): Promise<{ referrals: readonly ReferralView[] }> {
  const query = source ? `?source=${source}` : '';
  return request<{ referrals: readonly ReferralView[] }>('GET', `/api/v1/loyalty/referrals${query}`);
}

export function referralSummary(customerId: string): Promise<ReferralSummary> {
  return request<ReferralSummary>(
    'GET',
    `/api/v1/loyalty/referrals/customers/${encodeURIComponent(customerId)}`,
  );
}

export function attachReferral(inviteeCustomerId: string, code: string): Promise<ReferralView> {
  return request<ReferralView>('POST', '/api/v1/loyalty/referrals', {
    inviteeCustomerId,
    code,
    source: 'admin',
  });
}

export function referralPolicy(): Promise<ReferralPolicy> {
  return request<ReferralPolicy>('GET', '/api/v1/loyalty/referrals/policy');
}

export function saveReferralPolicy(policy: ReferralPolicy): Promise<ReferralPolicy> {
  return request<ReferralPolicy>('POST', '/api/v1/loyalty/referrals/policy', {
    enabled: policy.enabled,
    referrerRewardMinor: policy.referrerRewardMinor,
    inviteeRewardMinor: policy.inviteeRewardMinor,
    rewardExpiresInDays: policy.rewardExpiresInDays,
    minOrderMinor: policy.minOrderMinor,
  });
}
